import * as S from "./styles.ts";
import { Input, P, UniversalLink } from "@template/components";
import useDebouncedValue from "@/lib/helpers/hooks/useDebouncedValue.ts";
import { useEffect, useState } from "react";
import { FaqSearchResult } from "@/lib/types/faqs.ts";
import ArchStandaloneSectionComponent from "@template/blocks/ArchStandaloneSection/component";
import SvgSearch from "@template/icons/Search.tsx";

interface ISearchProps {
  mini?: boolean;
}

interface ISearchFaqProps {
  searchValue: string;
  setLoading: (loading: boolean) => void;
}

const searchFaq = async ({ searchValue, setLoading }: ISearchFaqProps) => {
  const apiUrl = `https://www.yay.com/api/faq/search/?search=${searchValue}`;
  try {
    setLoading(true);
    const result = await fetch(apiUrl);
    const data: FaqSearchResult[] = await result.json();
    return data;
  } catch (error) {
    console.error(`Error fetching search results: ${error}`);
  } finally {
    setLoading(false);
  }
};

const ResultsSection = ({
  results,
  active,
  mini,
}: {
  results: FaqSearchResult[];
  active: boolean;
  mini?: boolean;
}) => {
  const hasResults = active && results && results.length > 0;
  const noResults = active && !!results && results.length === 0;
  return (
    <S.SearchResultsWrapper
      $active={active}
      $mini={mini}
    >
      <S.SearchResultsContainer>
        {hasResults &&
          results.map((result) => (
            <S.SearchResult key={result.id}>
              <UniversalLink
                title={`Link to ${result.title}`}
                href={`/faq/${result.category_url_key}/${result.url_key}`}
              >
                <P $small={mini}>{result.title}</P>
              </UniversalLink>
            </S.SearchResult>
          ))}
        {noResults && (
          <S.NoResults>
            <P $small={mini}>Your search did not match any FAQ results.</P>
            <P $small={mini}>
              {" "}
              Need some assistance? Get in touch via{" "}
              <UniversalLink href="mailto:help@yay.com">
                help@yay.com.
              </UniversalLink>{" "}
              We'd love to help.
            </P>
          </S.NoResults>
        )}
      </S.SearchResultsContainer>
    </S.SearchResultsWrapper>
  );
};

export const Search = ({ mini }: ISearchProps) => {
  const [searchValue, setSearchValue] = useState("");
  const debouncedSearchValue = useDebouncedValue(searchValue, 500);
  const [results, setResults] = useState<FaqSearchResult[]>([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (debouncedSearchValue === "") {
      setResults([]);
      setLoading(false);
      setDropdownOpen(false);
      return;
    }
    if (debouncedSearchValue && debouncedSearchValue.length > 2) {
      searchFaq({
        searchValue: debouncedSearchValue,
        setLoading: setLoading,
      }).then((data) => {
        setResults(data);
        setDropdownOpen(true);
      });
    }
  }, [debouncedSearchValue]);

  return mini ? (
    <S.MiniSearchSectionContainer>
      <SvgSearch />
      <input
        placeholder="Search for articles"
        value={searchValue}
        onChange={(e) => setSearchValue(e.target.value)}
        onBlur={() => setSearchValue("")}
        autoFocus
      />
      <ResultsSection
        results={results}
        active={dropdownOpen}
        mini={true}
      />
    </S.MiniSearchSectionContainer>
  ) : (
    <S.SearchSectionContainer>
      <ArchStandaloneSectionComponent
        color="white"
        background="dark"
        style={{
          position: "absolute",
          top: 30,
        }}
      />
      <S.SearchContainer>
        <S.SearchCardContainer>
          <Input
            placeholder="Search for articles"
            value={searchValue}
            fullWidth
            onChange={setSearchValue}
            loading={loading}
            onBlur={() => setSearchValue("")}
          />

          <ResultsSection
            results={results}
            active={dropdownOpen}
          />
        </S.SearchCardContainer>
      </S.SearchContainer>
    </S.SearchSectionContainer>
  );
};
